import { CalendarDays, MessageCircle, Phone } from "lucide-react";
import { CONTACT } from "@/lib/site";

export default function Booking() {
  const waHref = `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(
    "Hi Sardar House, I'd like to check availability for a stay."
  )}`;

  return (
    <section
      id="book"
      data-testid="booking-section"
      className="relative bg-[color:var(--sh-charcoal)] py-24 sm:py-32 overflow-hidden"
    >
      {/* warm glow */}
      <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[720px] h-[420px] rounded-full bg-[color:var(--sh-amber-glow)] blur-3xl opacity-70 pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 sm:px-10">
        <div className="text-center max-w-2xl mx-auto sh-reveal">
          <div className="text-xs uppercase tracking-[0.35em] text-[color:var(--sh-amber)] mb-4">
            Book Your Stay
          </div>
          <h2 className="sh-display text-[color:var(--sh-cream)] text-4xl sm:text-5xl lg:text-6xl font-medium">
            Pick your dates.<br />We’ll keep the fire on.
          </h2>
          <p className="mt-6 text-[color:var(--sh-cream-dim)]">
            Real-time booking is arriving shortly. Until then, message us on
            WhatsApp or give us a call — we usually reply within a few minutes.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-5 gap-6 sh-reveal">
          {/* Calendly placeholder */}
          <div
            data-testid="calendly-placeholder"
            className="lg:col-span-3 rounded-3xl border border-dashed border-[color:var(--sh-line)] bg-[color:var(--sh-forest)]/40 p-8 sm:p-10 relative overflow-hidden"
          >
            <div className="absolute inset-0 sh-placeholder opacity-30 pointer-events-none" />
            <div className="relative flex flex-col h-full min-h-[320px]">
              <div className="flex items-center gap-3 opacity-70">
                <div className="w-11 h-11 rounded-2xl border border-[color:var(--sh-line)] flex items-center justify-center">
                  <CalendarDays size={20} strokeWidth={1.4} className="text-[color:var(--sh-cream-dim)]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--sh-cream-mute)]">
                    Coming soon
                  </div>
                  <div className="text-[color:var(--sh-cream)] sh-serif text-lg">
                    Availability calendar — powered by Calendly
                  </div>
                </div>
              </div>

              {/* fake calendar grid */}
              <div className="mt-8 grid grid-cols-7 gap-2 opacity-50 select-none">
                {Array.from({ length: 35 }).map((_, i) => (
                  <div
                    key={i}
                    className={`aspect-square rounded-lg border border-[color:var(--sh-line)] flex items-center justify-center text-[11px] text-[color:var(--sh-cream-mute)] ${
                      i % 9 === 0 ? "bg-[color:var(--sh-amber)]/10 text-[color:var(--sh-amber)]" : ""
                    }`}
                  >
                    {i + 1 <= 30 ? i + 1 : ""}
                  </div>
                ))}
              </div>

              <p className="mt-auto pt-8 text-sm text-[color:var(--sh-cream-dim)]">
                Real-time booking will be available here shortly.
              </p>
            </div>
          </div>

          {/* Fallback CTAs */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            <div className="sh-card rounded-3xl p-8 flex flex-col">
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--sh-amber)]">
                Reserve now
              </div>
              <h3 className="sh-display text-3xl text-[color:var(--sh-cream)] mt-3 leading-tight">
                Talk to us directly.
              </h3>
              <p className="text-sm text-[color:var(--sh-cream-dim)] mt-3">
                We answer WhatsApp and calls all day. Tell us your dates, the size
                of your group, and we’ll sort the rest.
              </p>
              <div className="mt-6 flex flex-col gap-3">
                <a
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-btn"
                  className="sh-amber-btn inline-flex items-center justify-center gap-2 px-6 py-4 text-sm"
                >
                  <MessageCircle size={16} strokeWidth={1.6} />
                  Message on WhatsApp
                </a>
                <a
                  href={`tel:${CONTACT.phoneRaw}`}
                  data-testid="call-btn"
                  className="sh-ghost-btn inline-flex items-center justify-center gap-2 px-6 py-4 text-sm"
                >
                  <Phone size={16} strokeWidth={1.6} />
                  Call {CONTACT.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
